import type {
  CollaborationPartnerProfile,
  CollaborationPolicy
} from './collaborationControlPlaneShared';

export type SupplierProfileNextAction =
  | 'add-principal-contact'
  | 'issue-admin-invite'
  | 'activate-supplier'
  | 'configure-reply-to'
  | 'authorise-template'
  | null;

export interface SupplierReadinessStep {
  id: string;
  label: string;
  detail: string;
  complete: boolean;
  waiting?: boolean;
}

export interface SupplierProfileReadiness {
  activationSteps: SupplierReadinessStep[];
  sendingSteps: SupplierReadinessStep[];
  nextAction: SupplierProfileNextAction;
  nextActivationStepId: string | null;
  principalContactRequiredForRecipientPolicy: boolean;
  hasPrincipalContact: boolean;
}

export const deriveSupplierProfileReadiness = (
  profile: CollaborationPartnerProfile,
  policy: CollaborationPolicy,
  now = new Date()
): SupplierProfileReadiness => {
  const hasPrincipalContact = profile.contacts.some(
    (contact) =>
      contact.status === 'active' &&
      contact.contactType === 'person' &&
      contact.isPrincipalContact
  );
  const principalContactRequiredForRecipientPolicy =
    policy.recipientPolicy === 'registered_contacts_only' || policy.recipientPolicy === 'named_delegates';
  const verifiedDomainRoute = Boolean(
    profile.partner.primaryDomain || profile.supplierOrganisation?.primaryDomain || profile.supplierOrganisation
  );
  const recipientRouteReady = principalContactRequiredForRecipientPolicy
    ? hasPrincipalContact
    : verifiedDomainRoute;
  const registrationComplete = Boolean(profile.supplierOrganisation);
  const currentInvitation = profile.adminInvites
    .filter((invite) => invite.status === 'pending')
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
  const invitationAgeMs = currentInvitation
    ? now.getTime() - new Date(currentInvitation.createdAt).getTime()
    : Number.POSITIVE_INFINITY;
  const invitationWaiting = Boolean(
    currentInvitation &&
      new Date(currentInvitation.expiresAt).getTime() > now.getTime() &&
      invitationAgeMs < 7 * 24 * 60 * 60 * 1000
  );
  const activationComplete = profile.partner.status === 'active';
  const replyToReady = Boolean(
    profile.partner.defaultReplyToEmail ||
      profile.templateReplyTos.length > 0 ||
      profile.hasInstanceDefaultReplyTo
  );
  const templateAccessReady = profile.templateAuthorisations.length > 0;

  let nextAction: SupplierProfileNextAction = null;
  if (!hasPrincipalContact && (!recipientRouteReady || !registrationComplete)) {
    // Verified-domain sending does not need a principal, but the existing
    // administrator-invitation authority still requires one.
    nextAction = 'add-principal-contact';
  } else if (!registrationComplete && !invitationWaiting) {
    nextAction = 'issue-admin-invite';
  } else if (!activationComplete && profile.partner.status === 'awaiting_activation') {
    nextAction = 'activate-supplier';
  } else if (activationComplete && !replyToReady) {
    nextAction = 'configure-reply-to';
  } else if (activationComplete && !templateAccessReady) {
    nextAction = 'authorise-template';
  }

  return {
    activationSteps: [
      {
        id: 'organisation',
        label: 'Supplier organisation recorded',
        detail: profile.partner.displayName,
        complete: true
      },
      {
        id: 'recipient-route',
        label: principalContactRequiredForRecipientPolicy
          ? 'Named recipient route ready'
          : 'Verified-domain recipient route ready',
        detail: principalContactRequiredForRecipientPolicy
          ? hasPrincipalContact
            ? 'An active named principal contact is available.'
            : 'Add an active named principal contact.'
          : profile.partner.primaryDomain || profile.supplierOrganisation?.primaryDomain
            ? `${profile.partner.primaryDomain || profile.supplierOrganisation?.primaryDomain} can be used by the verified-domain policy. A named principal is not an activation gate.`
            : registrationComplete
              ? 'The registered Microsoft organisation satisfies the verified-domain recipient route. A named principal is not an activation gate.'
              : 'Add the supplier domain used by the verified-domain recipient policy.',
        complete: recipientRouteReady
      },
      {
        id: 'administrator-contact',
        label: 'Administrator registration route ready',
        detail: registrationComplete
          ? 'Microsoft registration is already complete; the invitation contact is no longer a blocker.'
          : hasPrincipalContact
            ? 'The named principal contact can receive the administrator link.'
            : principalContactRequiredForRecipientPolicy
              ? 'Add the named principal contact used for recipient governance and registration.'
              : 'The verified-domain recipient route is ready, but a named person is still needed for the one-time administrator registration link.',
        complete: registrationComplete || hasPrincipalContact
      },
      {
        id: 'registration',
        label: 'Microsoft registration and supplier acceptance',
        detail: registrationComplete
          ? 'The supplier has registered and accepted the collaboration relationship.'
          : invitationWaiting
            ? 'The administrator link is current. Waiting for the supplier to register and accept.'
          : hasPrincipalContact
            ? 'Issue the administrator link and send it to the supplier.'
            : 'A named principal contact is required before an administrator link can be issued.',
        complete: registrationComplete,
        waiting: invitationWaiting
      },
      {
        id: 'activation',
        label: 'Supplier active',
        detail: activationComplete
          ? 'The supplier can receive authorised work.'
          : registrationComplete
            ? 'Review the completed registration and activate the supplier.'
            : 'Activation becomes available after registration and acceptance.',
        complete: activationComplete
      }
    ],
    sendingSteps: [
      {
        id: 'reply-to',
        label: 'Reply-to route available',
        detail: replyToReady
          ? 'Replies can be routed when work is sent.'
          : 'Configure a reply-to before sending. This does not block supplier activation.',
        complete: replyToReady
      },
      {
        id: 'template-access',
        label: 'Work access configured',
        detail: templateAccessReady
          ? `${profile.templateAuthorisations.length} template ${profile.templateAuthorisations.length === 1 ? 'rule' : 'rules'} available.`
          : 'Authorise the templates this supplier may receive before sending. This does not block activation.',
        complete: templateAccessReady
      }
    ],
    nextAction,
    nextActivationStepId:
      nextAction === 'add-principal-contact'
        ? principalContactRequiredForRecipientPolicy
          ? 'recipient-route'
          : 'administrator-contact'
        : nextAction === 'issue-admin-invite'
          ? 'registration'
          : nextAction === 'activate-supplier'
            ? 'activation'
            : null,
    principalContactRequiredForRecipientPolicy,
    hasPrincipalContact
  };
};
